import BaseTrackingService from '../BaseTrackingService';
import { EVENTS } from './checkoutStepsEvents';

class CheckoutTrackingService extends BaseTrackingService {
  static EVENT_CHECKOUT_STARTED = EVENTS.CHECKOUT_STARTED;
  static EVENT_CHECKOUT_STEP_2_ADVANCED = EVENTS.CHECKOUT_STEP_2_ADVANCED;
  static EVENT_CHECKOUT_STEP_2_ADVANCED_FAILED =
    EVENTS.CHECKOUT_STEP_2_ADVANCED_FAILED;
  static EVENT_CHECKOUT_SIGN_UP_ATTEMPT = EVENTS.CHECKOUT_SIGN_UP_ATTEMPT;
  static EVENT_CHECKOUT_CARD_TOKENIZATION_SUCCESS =
    EVENTS.CHECKOUT_CARD_TOKENIZATION_SUCCESS;
  static EVENT_CHECKOUT_CARD_TOKENIZATION_ERROR =
    EVENTS.CHECKOUT_CARD_TOKENIZATION_ERROR;
  static EVENT_CHECKOUT_PAYMENT_FAILED = EVENTS.CHECKOUT_PAYMENT_FAILED;

  static EVENT_CHECKOUT_PIX_PAYMENT_CLICKED =
    EVENTS.CHECKOUT_PIX_PAYMENT_CLICKED;
  static EVENT_CHECKOUT_PIX_PAYMENT_PAID = EVENTS.CHECKOUT_PIX_PAYMENT_PAID;
  static EVENT_CHECKOUT_PIX_PAYMENT_FAILED =
    EVENTS.CHECKOUT_PIX_PAYMENT_FAILED;
  static EVENT_CHECKOUT_TRY_AGAIN_CLICKED = EVENTS.CHECKOUT_TRY_AGAIN_CLICKED;
  static EVENT_CHECKOUT_ACCESS_PLATFORM_CLICKED =
    EVENTS.CHECKOUT_ACCESS_PLATFORM_CLICKED;

  constructor() {
    super('CheckoutTrackingService');
  }

  /**
   * Monta propriedades para `trackCheckoutCardTokenizationSuccess` em checkoutStepsEvents.ts.
   * @param {Object} formikValues - Valores do formik
   * @param {Object} tokenData - Dados do token gerado
   * @param {Object} coupom - Dados do cupom
   * @param {number} defaultTrialDays - Dias de trial padrão
   * @returns {Object} Propriedades para funções track* de checkout
   */
  prepareCardTokenizationSuccessForAnalytics(
    formikValues,
    tokenData,
    coupom = {},
    defaultTrialDays = 7,
  ) {
    const userData = this._buildUserData(formikValues);
    const cardData = this._buildCardData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupom,
      defaultTrialDays,
    );

    return this._buildCardTokenizationSuccessTrackingData(
      cardData,
      userData,
      tokenData,
      checkoutData,
    );
  }

  /**
   * @param {Object} cardData - Dados do cartão
   * @param {Object} userData - Dados do usuário
   * @param {Object} tokenData - Dados do token gerado
   * @param {Object} checkoutData - Dados do checkout (plano, cupom, etc.)
   * @returns {Object} Payload do evento checkout_card_tokenization_success
   */
  _buildCardTokenizationSuccessTrackingData(
    cardData,
    userData,
    tokenData,
    checkoutData = {},
  ) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Informações do cartão
      card_hash: tokenData.id || tokenData.card_hash,
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      provider: 'pagarme',
      status: 'success',

      // Informações adicionais para Metabase
      event_type:
        CheckoutTrackingService.EVENT_CHECKOUT_CARD_TOKENIZATION_SUCCESS,
      conversion_value: 1,
      is_first_card: true, // No checkout sempre é o primeiro cartão
      checkout_step: 'step2',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutCardTokenizationError` em checkoutStepsEvents.ts.
   */
  prepareCheckoutCardTokenizationErrorForAnalytics(
    formikValues,
    error,
    coupom = {},
    defaultTrialDays = 7,
  ) {
    const userData = this._buildUserData(formikValues);
    const cardData = this._buildCardData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupom,
      defaultTrialDays,
    );

    return this._buildCardTokenizationErrorTrackingData(
      cardData,
      userData,
      error,
      checkoutData,
    );
  }

  /**
   * @param {Object} cardData - Dados do cartão
   * @param {Object} userData - Dados do usuário
   * @param {Object} error - Erro ocorrido
   * @param {Object} checkoutData - Dados do checkout
   */
  _buildCardTokenizationErrorTrackingData(
    cardData,
    userData,
    error,
    checkoutData = {},
  ) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Informações do cartão (mascaradas por segurança)
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),

      // Informações do erro
      error_message: error?.error_message || 'Unknown error',
      error_code: error?.error_code || 'UNKNOWN_ERROR',
      error_type: error?.error_type || 'tokenization_error',
      cors_details: error?.cors_details || null,
      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      provider: 'pagarme',
      status: 'error',

      // Informações adicionais para Metabase
      event_type:
        CheckoutTrackingService.EVENT_CHECKOUT_CARD_TOKENIZATION_ERROR,
      conversion_value: 0,
      is_first_card: true,
      checkout_step: 'step2',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutStarted` em checkoutStepsEvents.ts.
   * @param {Object} formikValues - Valores do formik
   * @param {Object} coupom - Dados do cupom
   * @returns {Object} Propriedades para funções track* de checkout
   */
  prepareCheckoutStartedForAnalytics(formikValues, coupom = {}) {
    const userData = this._buildUserData(formikValues);
    const checkoutData = this._buildCheckoutData(formikValues, coupom);

    return this._buildCheckoutStartedTrackingData(userData, checkoutData);
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @returns {Object} Payload do evento checkout_started
   */
  _buildCheckoutStartedTrackingData(userData, checkoutData = {}) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      event_type: CheckoutTrackingService.EVENT_CHECKOUT_STARTED,
      conversion_value: 0,
      checkout_step: 'step1',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutStep2Advanced` em checkoutStepsEvents.ts.
   * @param {Object} formikValues - Valores do formik
   * @param {Object} coupom - Dados do cupom
   * @returns {Object} Propriedades para funções track* de checkout
   */
  prepareCheckoutStep2AdvancedForAnalytics(formikValues, coupom = {}) {
    const userData = this._buildUserData(formikValues);
    const checkoutData = this._buildCheckoutData(formikValues, coupom);

    return this._buildCheckoutStep2AdvancedTrackingData(userData, checkoutData);
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @returns {Object} Payload do evento checkout_step_2_advanced
   */
  _buildCheckoutStep2AdvancedTrackingData(userData, checkoutData = {}) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      event_type: CheckoutTrackingService.EVENT_CHECKOUT_STEP_2_ADVANCED,
      conversion_value: 0,
      checkout_step: 'step1',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutStep2AdvancedFailed` em checkoutStepsEvents.ts.
   * @param {Object} formikValues - Valores do formik
   * @param {Object|null} coupom - Dados do cupom
   * @param {Object} error - Dados do erro
   * @returns {Object} Propriedades para funções track* de checkout
   */
  prepareCheckoutStep2AdvancedFailedForAnalytics(
    formikValues,
    coupom = null,
    error,
  ) {
    const userData = this._buildUserData(formikValues);
    const checkoutData = coupom
      ? this._buildCheckoutData(formikValues, coupom)
      : null;

    return this._buildCheckoutStep2AdvancedFailedTrackingData(
      userData,
      checkoutData,
      error,
    );
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object|null} checkoutData - Dados do checkout
   * @param {Object} error - Dados do erro
   * @returns {Object} Payload do evento checkout_step_2_advanced_failed
   */
  _buildCheckoutStep2AdvancedFailedTrackingData(
    userData,
    checkoutData = null,
    error,
  ) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      ...(checkoutData && {
        plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
        plan_name: checkoutData.plan_name,
        plan_amount: checkoutData.plan_amount,
        plan_type: checkoutData.plan_type,
        coupon_code: checkoutData.coupon_code,
        has_compra_garantida: checkoutData.has_compra_garantida,
      }),

      // Informações do erro
      error_message: error?.message || 'Unknown error',
      error_code: error?.code || 'UNKNOWN_ERROR',
      error_type: error?.type || 'tokenization_error',

      ...(error?.type === 'validation_error' && {
        validation_errors: error?.validation_errors,
        failed_field: error?.failed_field,
        validation_failed: true,
      }),

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      event_type: CheckoutTrackingService.EVENT_CHECKOUT_STEP_2_ADVANCED_FAILED,
      conversion_value: 0,
      checkout_step: 'step1',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutSignUpAttempt` em checkoutStepsEvents.ts.
   * @param {Object} formikValues - Valores do formik
   * @param {Object} coupom - Dados do cupom
   * @param {number} defaultTrialDays - Dias de trial padrão
   * @returns {Object} Propriedades para funções track* de checkout
   */
  prepareCheckoutSignUpAttemptForAnalytics(
    formikValues,
    coupom = {},
    defaultTrialDays = 7,
  ) {
    const userData = this._buildUserData(formikValues);
    const cardData = this._buildCardData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupom,
      defaultTrialDays,
    );

    return this._buildCheckoutSignUpAttemptTrackingData(
      userData,
      checkoutData,
      cardData,
    );
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @param {Object} cardData - Dados do cartão
   * @returns {Object} Payload do evento checkout_sign_up_attempt
   */
  _buildCheckoutSignUpAttemptTrackingData(
    userData,
    checkoutData = {},
    cardData = {},
  ) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Informações do cartão
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      event_type: CheckoutTrackingService.EVENT_CHECKOUT_SIGN_UP_ATTEMPT,
      conversion_value: 0,
      checkout_step: 'step2',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutPaymentFailed` em checkoutStepsEvents.ts.
   * @param {Object} formikValues - Valores do formik
   * @param {Object} failedReason - Dados do erro de pagamento
   * @param {Object} coupom - Dados do cupom
   * @param {number} defaultTrialDays - Dias de trial padrão
   * @returns {Object} Propriedades para funções track* de checkout
   */
  preparePaymentFailedForAnalytics(
    formikValues,
    failedReason,
    coupom = {},
    defaultTrialDays = 7,
  ) {
    const userData = this._buildUserData(formikValues);
    const cardData = this._buildCardData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupom,
      defaultTrialDays,
    );

    return this._buildPaymentFailedTrackingData(
      userData,
      checkoutData,
      failedReason,
      cardData,
    );
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @param {Object} failedReason - Dados do erro de pagamento
   * @param {Object} cardData - Dados do cartão (opcional)
   * @returns {Object} Payload do evento checkout_payment_failed
   */
  _buildPaymentFailedTrackingData(
    userData,
    checkoutData,
    failedReason,
    cardData = {},
  ) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Informações do cartão (se disponível)
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),

      // Informações do erro de pagamento
      error_message:
        failedReason.message ||
        failedReason?.error_message ||
        'Unknown payment error',
      error_details:
        failedReason.details ||
        failedReason?.error_code ||
        'No details available',
      error_code: failedReason?.error_code || 'unknown',
      error_type: failedReason?.error_type || 'payment_failed',

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      provider: 'pagarme',
      status: 'payment_failed',

      // Informações adicionais para Metabase
      event_type: CheckoutTrackingService.EVENT_CHECKOUT_PAYMENT_FAILED,
      conversion_value: 0,
      is_first_card: true,
      checkout_step: 'step2',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutPixPaymentClicked` em checkoutStepsEvents.ts.
   */
  prepareCheckoutPixPaymentClickedForAnalytics(
    formikValues,
    coupom = {},
    defaultTrialDays = 7,
  ) {
    const userData = this._buildUserData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupom,
      defaultTrialDays,
    );

    return this._pixPaymentClicked(userData, checkoutData);
  }

  /**
   * Método interno para rastrear clique em "Pagar com Pix"
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   */
  _pixPaymentClicked(userData, checkoutData = {}) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),
      customer_document_number: userData.document || '',
      customer_name: userData.name || '',
      customer_email: userData.email || '',

      // Informações da transação
      transaction_id: '',
      transaction_status: '',

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      event_type: CheckoutTrackingService.EVENT_CHECKOUT_PIX_PAYMENT_CLICKED,
      conversion_value: 0,
      checkout_step: 'step2',
      action: 'pix_payment_clicked',
      payment_method: 'pix',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutPixPaymentFailed` em checkoutStepsEvents.ts.
   */
  prepareCheckoutPixPaymentFailedForAnalytics(
    formikValues,
    error,
    coupom = {},
    defaultTrialDays = 7,
  ) {
    const userData = this._buildUserData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupom,
      defaultTrialDays,
    );

    return this._pixPaymentFailedTrackingData(userData, checkoutData, error);
  }

  /**
   * Monta o payload de `checkout_pix_payment_failed` (falha na geração do Pix).
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @param {Object} error - Erro ocorrido
   */
  _pixPaymentFailedTrackingData(userData, checkoutData = {}, error = {}) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),
      customer_document_number: userData.document || '',
      customer_name: userData.name || '',
      customer_email: userData.email || '',

      // Informações da transação
      transaction_id:
        error?.transaction_id || error?.response?.data?.transaction_id || '',
      transaction_status:
        error?.transaction_status || error?.response?.data?.status || 'failed',

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Informações do erro
      error_message: error?.error_message || error?.message || 'Unknown error',
      error_code:
        error?.error_code || error?.response?.status || 'UNKNOWN_ERROR',
      error_type: error?.error_type || 'pix_payment_failed',
      error_details:
        error?.error_details || error?.response?.data?.message || null,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      provider: 'pagarme_v5',
      status: 'error',
      event_type: CheckoutTrackingService.EVENT_CHECKOUT_PIX_PAYMENT_FAILED,
      conversion_value: 0,
      checkout_step: 'step2',
      action: 'pix_payment_failed',
      payment_method: 'pix',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutPixPaymentPaid` em checkoutStepsEvents.ts.
   */
  prepareCheckoutPixPaymentPaidForAnalytics(
    transactionData,
    userData,
    checkoutData = {},
    origin = 'checkout',
  ) {
    return this._buildPixPaymentPaidTrackingData(
      transactionData,
      userData,
      checkoutData,
      origin,
    );
  }

  /**
   * Monta o payload de `checkout_pix_payment_paid`.
   * @param {Object} transactionData - Dados da transação Pix
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @param {string} origin - Origem do evento
   */
  _buildPixPaymentPaidTrackingData(
    transactionData,
    userData,
    checkoutData = {},
    origin = 'checkout',
  ) {
    // Prioriza a estrutura do exemplo fornecido
    const gatewayData = transactionData?.gatewayData || {};
    const lastTransaction = gatewayData?.last_transaction || {};
    const customer = gatewayData?.customer || {};

    // Informações da transação - prioriza gatewayData.last_transaction
    const transaction_id =
      lastTransaction?.id ||
      transactionData?.transactionId ||
      transactionData?.id ||
      '';

    const transaction_status =
      lastTransaction?.status ||
      gatewayData?.status ||
      transactionData?.status ||
      'paid';

    const created_at =
      lastTransaction?.created_at ||
      gatewayData?.created_at ||
      transactionData?.createdAt ||
      new Date().toISOString();

    const updated_at =
      lastTransaction?.updated_at ||
      gatewayData?.updated_at ||
      transactionData?.updatedAt ||
      new Date().toISOString();

    // Valor do pagamento - prioriza gatewayData
    const amount =
      gatewayData?.amount ||
      lastTransaction?.amount ||
      gatewayData?.paid_amount ||
      checkoutData?.plan_amount ||
      transactionData?.basePrice ||
      0;

    // Informações do cliente - prioriza gatewayData.customer
    const customer_document_number =
      customer?.document || userData?.document || '';

    const customer_name = customer?.name || userData?.name || '';

    const customer_email = customer?.email || userData?.email || '';

    const trackingData = {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),
      user_email: userData.email || customer_email,
      user_name: userData.name || customer_name,
      customer_document_number: customer_document_number,
      customer_name: customer_name,
      customer_email: customer_email,

      // Informações da transação
      transaction_id: transaction_id,
      transaction_status: transaction_status,
      created_at: created_at,
      updated_at: updated_at,
      payment_method: 'pix',
      amount: amount,

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      provider: 'pagarme_v5',
      status: 'paid',
      event_type: CheckoutTrackingService.EVENT_CHECKOUT_PIX_PAYMENT_PAID,
      conversion_value: 1,
      checkout_step: origin === 'logged-area' ? undefined : 'step2',
      action: 'pix_payment_paid',
      origin: origin,
    };

    return trackingData;
  }

  /**
   * Monta propriedades para `trackCheckoutTryAgainClicked` em checkoutStepsEvents.ts.
   */
  prepareCheckoutTryAgainClickedForAnalytics(
    formikValues,
    coupom = {},
    defaultTrialDays = 7,
  ) {
    const userData = this._buildUserData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupom,
      defaultTrialDays,
    );

    return this._buildTryAgainClickedTrackingData(userData, checkoutData);
  }

  /**
   * Monta o payload de `checkout_try_again_clicked`.
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   */
  _buildTryAgainClickedTrackingData(userData, checkoutData = {}) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),
      customer_document_number: userData.document || '',
      customer_name: userData.name || '',
      customer_email: userData.email || '',

      // Informações da transação
      transaction_id: '',
      transaction_status: '',

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      event_type: CheckoutTrackingService.EVENT_CHECKOUT_TRY_AGAIN_CLICKED,
      conversion_value: 0,
      checkout_step: 'step2',
      action: 'try_again_clicked',
    };
  }

  /**
   * Monta propriedades para `trackCheckoutAccessPlatformClicked` em checkoutStepsEvents.ts.
   */
  prepareCheckoutAccessPlatformClickedForAnalytics(
    formikValues,
    coupom = {},
    defaultTrialDays = 7,
    origin,
  ) {
    const userData = this._buildUserData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupom,
      defaultTrialDays,
    );

    return this._buildAccessPlatformClickedTrackingData(
      userData,
      checkoutData,
      origin,
    );
  }

  /**
   * Monta o payload de `checkout_access_platform_clicked`.
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @param {string} origin - Origem do clique
   */
  _buildAccessPlatformClickedTrackingData(
    userData,
    checkoutData = {},
    origin = 'failed-modal-checkout',
  ) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),
      customer_document_number: userData.document || '',
      customer_name: userData.name || '',
      customer_email: userData.email || '',

      // Informações da transação
      transaction_id: '',
      transaction_status: '',

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_gateway_id: checkoutData.plan_gateway_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      event_type:
        CheckoutTrackingService.EVENT_CHECKOUT_ACCESS_PLATFORM_CLICKED,
      conversion_value: 1,
      checkout_step: 'step2',
      action: 'access_platform_clicked',
      status: 'success',
      origin: origin,
    };
  }
}

// Exportar instância singleton
export default new CheckoutTrackingService();
